import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiBaseUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';

export interface GetCountriesPhoenixResponse {
  data: Country[];
}

export interface Country {
  id: string;
  name: string;
  iso_code: string;
  iso_code_3: string;
  numeric_code: number;
}

export interface ManualPayinWithBankAccountBody {
  return_path?: string;
  bank_account: {
    iban: string;
    first_name: string;
    last_name: string;
    owner_address: {
      city: string;
      address_line_1: string;
      address_line_2?: string;
      country: string;
      postal_code: string;
    };
  };
  amount: number;
}

export interface SetupMangoPayDirectDebitResidentRequest {
  params: {
    paymentProcessorMode: 'live' | 'test';
    tenant: string;
    resident_id: string;
  };
  body: {
    bank_account: (
      | {
          // New Recipient API format
          first_name: string;
          last_name: string;
        }
      | {
          // Old Bank Account API format (backward compatibility)
          owner_name: string;
        }
    ) & {
      iban: string;
      owner_address: {
        city: string;
        address_line_1: string;
        address_line_2?: string;
        country: string;
        postal_code: string;
      };
    };
  };
}

export interface SetupMangoPayDirectDebitResponse {
  data: {
    bank_account: {
      id: string;
      iban: string;
      owner_name: string;
      owner_address: {
        city: string;
        address_line_1: string;
        address_line_2?: string;
        region: string;
        country: string;
        postal_code: string;
      };
    };
    mandate: {
      id: string;
      status: 'active' | 'inactive' | 'pending';
      document_url: string;
    };
  };
}

export interface ResidentFamilyMemberResponse {
  data: {
    source_of_mandate: null | 'resident' | 'family_member';
  };
}

export interface IFamilyMemberPhoenixApi extends Api {
  getCountries(dataIn: {
    params: {
      tenant: string;
    };
  }): Promise<GetCountriesPhoenixResponse>;

  getMangoPayDirectDebit(dataIn: {
    params: {
      paymentProcessorMode: 'live' | 'test';
    };
  }): Promise<SetupMangoPayDirectDebitResponse>;

  getMangoPayDirectDebitResident(dataIn: {
    params: {
      paymentProcessorMode: 'live' | 'test';
      tenant: string;
      resident_id: string;
    };
  }): Promise<SetupMangoPayDirectDebitResponse>;

  setupMangoPayDirectDebitResident(
    dataIn: SetupMangoPayDirectDebitResidentRequest
  ): Promise<SetupMangoPayDirectDebitResponse>;

  getFamilyMemberResident(dataIn: {
    params: {
      tenant: string;
      resident_id: string;
    };
  }): Promise<ResidentFamilyMemberResponse>;

  updateFamilyMemberResident(dataIn: {
    params: {
      tenant: string;
      resident_id: string;
    };
    body: {
      source_of_mandate: 'resident' | 'family_member';
    };
  }): Promise<ResidentFamilyMemberResponse>;

  cancelFamilyMemberMandate(dataIn: {
    params: {
      paymentProcessorMode: 'live' | 'test';
    };
  }): Promise<void>;

  cancelResidentMandate(dataIn: {
    params: {
      paymentProcessorMode: 'live' | 'test';
      tenant: string;
      resident_id: string;
    };
  }): Promise<void>;

  manualPayin(dataIn: {
    params: {
      resident_id: string;
      tenant: string;
    };
    body: {
      amount: number;
    };
  }): Promise<void>;

  manualPayinWithBankAccount(dataIn: {
    params: {
      resident_id: string;
      tenant: string;
    };
    body: ManualPayinWithBankAccountBody;
  }): Promise<SetupMangoPayDirectDebitResponse>;
}

export class FamilyMemberPhoenixApi implements IFamilyMemberPhoenixApi {
  baseUrl: string;
  headers: Headers;

  public getCountries: IFamilyMemberPhoenixApi['getCountries'];
  public getMangoPayDirectDebit: IFamilyMemberPhoenixApi['getMangoPayDirectDebit'];
  public setupMangoPayDirectDebitResident: IFamilyMemberPhoenixApi['setupMangoPayDirectDebitResident'];
  public getMangoPayDirectDebitResident: IFamilyMemberPhoenixApi['getMangoPayDirectDebitResident'];
  public getFamilyMemberResident: IFamilyMemberPhoenixApi['getFamilyMemberResident'];
  public updateFamilyMemberResident: IFamilyMemberPhoenixApi['updateFamilyMemberResident'];
  public cancelFamilyMemberMandate: IFamilyMemberPhoenixApi['cancelFamilyMemberMandate'];
  public cancelResidentMandate: IFamilyMemberPhoenixApi['cancelResidentMandate'];
  public manualPayin: IFamilyMemberPhoenixApi['manualPayin'];
  public manualPayinWithBankAccount: IFamilyMemberPhoenixApi['manualPayinWithBankAccount'];

  constructor(public authContext: AuthenticationContext, apiUrl?: string) {
    this.baseUrl = getPhoenixApiBaseUrl(apiUrl);
    this.headers = new Headers({
      'content-type': 'application/json',
    });

    this.getCountries = get({
      authContext,
      url: ({ tenant }) => `${this.baseUrl}/api/${tenant}/countries`,
    });

    this.getMangoPayDirectDebit = get({
      authContext,
      url: () => `${this.baseUrl}/api/family-member/mangopay/mandate`,
      headers: (params) => {
        const headers = this.headers;
        headers.set('x-pn-stripemode', params.paymentProcessorMode);
        return headers;
      },
    });

    this.getMangoPayDirectDebitResident = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/api/family-member/${params.tenant}/${params.resident_id}/mangopay/mandate`,
      headers: (params) => {
        const headers = this.headers;
        headers.set('x-pn-stripemode', params.paymentProcessorMode);
        return headers;
      },
    });

    this.setupMangoPayDirectDebitResident = modify({
      authContext,
      headers: (params) => {
        const headers = this.headers;
        headers.set('x-pn-stripemode', params.paymentProcessorMode);
        return headers;
      },
      url: (params) =>
        `${this.baseUrl}/api/family-member/${params.tenant}/${params.resident_id}/mangopay/mandate`,
      method: 'POST',
    });

    this.getFamilyMemberResident = get({
      authContext,
      url: (params) =>
        `${this.baseUrl}/api/family-member/${params.tenant}/${params.resident_id}`,
    });

    this.updateFamilyMemberResident = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/api/family-member/${params.tenant}/${params.resident_id}`,
      method: 'PATCH',
    });

    this.cancelFamilyMemberMandate = modify({
      authContext,
      url: () => `${this.baseUrl}/api/family-member/mangopay/mandate`,
      method: 'DELETE',

      headers: (params) => {
        const headers = this.headers;
        headers.set('x-pn-stripemode', params.paymentProcessorMode);
        return headers;
      },
    });

    this.cancelResidentMandate = modify({
      authContext,
      url: (params) =>
        `${this.baseUrl}/api/family-member/${params.tenant}/${params.resident_id}/mangopay/mandate`,
      method: 'DELETE',

      headers: (params) => {
        const headers = this.headers;
        headers.set('x-pn-stripemode', params.paymentProcessorMode);
        return headers;
      },
    });

    this.manualPayin = modify({
      authContext,
      url: ({ resident_id, tenant }) =>
        `${this.baseUrl}/api/family-member/${tenant}/${resident_id}/mangopay/payin`,
      method: 'POST',
    });

    this.manualPayinWithBankAccount = modify({
      authContext,
      url: ({ resident_id, tenant }) =>
        `${this.baseUrl}/api/family-member/${tenant}/${resident_id}/mangopay/payin`,
      method: 'POST',
    });
  }
}
